import * as React from 'react';
import { DownloadSimpleIcon, XIcon, PlusSquareIcon, EyeIcon } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
import { handleDownload, extractPathAfterBulk, isRestrictedFileType } from '@/shared/functions';
import { Tooltip } from "@heroui/tooltip";
import { useEffect, useRef, useContext, useState } from 'react';
import Select from "react-select";
import { Source, SourceObject } from '@/shared/types';
import { SourcesSelectStyles } from '@/shared/styleDefs';
import { useDebounce } from 'use-debounce';
import { FeaturesContext } from '@/app/contexts';
import { MediaType, MediaObject } from '@/shared/types';
import { UsableMediaTypes } from '@/shared/constants';
import MediaDisplay from '../ui/mediaDisplay';
import { getMediaType } from '@/shared/functions';
import { FullScreenContext } from '@/app/contexts';
export const sourceObjectTypes = ['image', 'video', 'audio', 'pdf', 'url'];
type OptionType = {
  value: SourceObject;
  label: string;
};

/** Page may be stored as sourceContext.pageNumber, sourceContext.page (legacy), or top-level pageNumber. */
const resolveSourcePageRef = (source: SourceObject) => source.sourceContext?.pageNumber ?? source.sourceContext?.page ?? source.pageNumber;

/** Audio/video: only start/end (or nothing). Other types: page only (never time indices). */
function formatSimplifiedSourceRowSuffix(sourceObject: SourceObject): string {
  const mt = getMediaType(sourceObject, sourceObject.sourceInfo);
  if (mt === 'audio' || mt === 'video') {
    const parts: string[] = [];
    if (sourceObject.sourceContext?.startTime != null && sourceObject.sourceContext.startTime !== '') {
      parts.push(`Start: ${sourceObject.sourceContext.startTime}`);
    }
    if (sourceObject.sourceContext?.endTime != null && sourceObject.sourceContext.endTime !== '') {
      parts.push(`End: ${sourceObject.sourceContext.endTime}`);
    }
    return parts.length ? `, ${parts.join(', ')}` : '';
  }
  const pageRef = resolveSourcePageRef(sourceObject);
  return pageRef != null && pageRef !== '' ? `, Page ${pageRef}` : '';
}
function formatSimplifiedChatSearchOptionLabel(source: SourceObject): string {
  const mt = getMediaType(source, source.sourceInfo);
  if (mt === 'audio' || mt === 'video') {
    const bits: string[] = [];
    if (source.sourceContext?.startTime) bits.push(`Time Index: ${source.sourceContext.startTime}`);
    if (source.sourceContext?.endTime) bits.push(`End: ${source.sourceContext.endTime}`);
    const suffix = bits.length ? `, ${bits.join(', ')}` : '';
    return `${source.sourceUrl}${suffix}`;
  }
  const pageRef = resolveSourcePageRef(source);
  const pageSuffix = pageRef != null && pageRef !== '' ? `, page ${pageRef}` : '';
  return `${source.sourceUrl}${pageSuffix}`;
}
const limitFilter = (array: SourceObject[], condition: (item: SourceObject) => boolean, maxSize: number) => {
  if (!maxSize || maxSize > array.length) {
    maxSize = array.length;
  }
  let count = 0;
  let i = 0;
  let filteredArray: SourceObject[] = [];
  while (count < maxSize && i < array.length) {
    if (condition(array[i])) {
      filteredArray.push(array[i]);
      count++;
    }
    i++;
  }
  return filteredArray;
};
interface SimlifiedSourcesProps {
  value: SourceObject[];
  slide?: number;
  column?: string;
  isInModal?: boolean;
  isInPresentationView?: boolean;
  handleChange?: any;
  editingEnabled?: boolean;
  caseId?: string;
  handleSourceChanges?: any;
  sourcesFromChat?: SourceObject[];
}
const SimplifiedSources = (props: SimlifiedSourcesProps) => {
  const {
    value,
    slide,
    column,
    isInModal,
    isInPresentationView,
    handleChange,
    editingEnabled,
    caseId,
    handleSourceChanges,
    sourcesFromChat
  } = props;
  const [sourceFromSessionStorage, setSourceFromSessionStorage] = useState<Source | null>(null);
  const [newSources, setNewSources] = useState<SourceObject[]>(value);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [sourcesForDropdown, setSourcesForDropdown] = useState<SourceObject[]>([]);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState(-1);
  const {
    isFullscreen
  } = useContext(FullScreenContext);
  const [searchTerm] = useDebounce(inputValue, 500);
  const selectRef = useRef<HTMLDivElement>(null);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const getInputValue = (elem: Element) => {
    if (elem) {
      const inputValue = elem.getAttribute('data-value');
      setInputValue(inputValue);
    }
    return null;
  };
  useEffect(() => {
    if (isFullscreen) {
      setPreviewPopoverOpen(-1);
    }
  }, [isFullscreen]);
  useEffect(() => {
    if (selectRef.current) {
      const inputContainer = selectRef.current.getElementsByClassName('react-select__input-container')[0];
      const handler = (e: KeyboardEvent) => getInputValue(inputContainer);
      if (inputContainer) {
        window.addEventListener('keyup', handler);
        return () => {
          window.removeEventListener('keyup', handler);
        };
      }
    }
  }, [selectRef.current]);
  useEffect(() => {
    if (sourcesFromChat && searchTerm && searchTerm.length > 2) {
      const filteredSources = limitFilter(sourcesFromChat, source => {
        return !!source.sourceUrl && source.sourceUrl.toLowerCase().includes(searchTerm.toLowerCase());
      }, 100);
      setSourcesForDropdown(filteredSources);
    }
  }, [sourcesFromChat, searchTerm]);
  const availableSource = sessionStorage.getItem(`source`);
  useEffect(() => {
    const sourcesIn = value.filter(source => source.sourceUrl && source.sourceUrl.trim() !== "");
    setNewSources(sourcesIn);
  }, [value]);
  useEffect(() => {
    if (availableSource) {
      const sourceFromSessionStorage = JSON.parse(availableSource);
      const sourceCaseId = sourceFromSessionStorage?.caseId?.trim();
      if (sourceCaseId === caseId) {
        setSourceFromSessionStorage(sourceFromSessionStorage.source);
      } else {
        setSourceFromSessionStorage(null);
      }
    } else {
      setSourceFromSessionStorage(null);
    }
  }, [availableSource, caseId]);
  const addSourceFromSessionStorage = () => {
    if (sourceFromSessionStorage) {
      const moreSources = newSources ? [...newSources] : [];
      const sourceType = extractPathAfterBulk(sourceFromSessionStorage.sourceFilePath || '').sourceType || 'Unknown';
      const sourceObject = {
        sourceType: sourceType,
        sourceUrl: sourceFromSessionStorage.sourceFilePath,
        truncatedFilePath: sourceFromSessionStorage.truncatedFilePath,
        sourceContext: sourceFromSessionStorage.sourceContext,
        pageNumber: sourceFromSessionStorage.pageNumber
      };
      moreSources.push(sourceObject);
      setNewSources(moreSources);
      handleSourceChanges(moreSources);
    }
  };
  const onSourceSelect = (source: SourceObject) => {
    const moreSources = newSources ? [...newSources] : [];
    if (source) {
      moreSources.push(source);
      setNewSources(moreSources);
      handleSourceChanges(moreSources);
    }
  };
  const removeSource = (index: number) => {
    const lessSources = newSources.filter((_, i) => i !== index);
    setNewSources(lessSources);
    handleSourceChanges(lessSources);
  };
  if (isInModal || isInPresentationView) {
    return <div className="box-border flex flex-col justify-left w-full">
        {editingEnabled && <>  
            {sourcesFromChat && sourcesFromChat.length > 0 && <div className='flex flex-row justify-start items-center gap-3 w-full p-0 m-0'>
                <div ref={selectRef} className='box-border whitespace-nowrap z-900'>
                  <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">Sources</label>
                  <Select<OptionType, false> styles={SourcesSelectStyles as any} className="zIndexSelect-19" classNamePrefix='react-select' aria-label='Add sources from Assistant' placeholder='Type at least 3 characters to search' noOptionsMessage={() => 'No matching sources found'} options={sourcesForDropdown?.map((source, index) => ({
              value: source,
              label: formatSimplifiedChatSearchOptionLabel(source)
            }))} onChange={option => option && onSourceSelect(option.value)} components={{
              Option: ({
                data,
                innerProps
              }: {
                data: OptionType;
                innerProps: any;
              }) => <div {...innerProps} className="box-border p-0 flex flex-row justify-left bg-[#0F1D32] rounded-sm">
                          <div className="box-border w-full rounded-sm py-[5px] px-[3px] bg-slate-800 text-[14px] cursor-pointer
                            dir-rtl whitespace-nowrap truncate border-1 border-white border-solid opacity-100% z-1000">
                            &lrm;{data.label}
                          </div>
                        </div>
            }} />
                </div>
                {sourceFromSessionStorage && <div className='flex flex-row justify-left items-center gap-1 w-[20px] p-0 m-0'>
                    <Tooltip className='text-white bg-slate-600 rounded-sm p-2' content='Add last source copied from Assistant' placement='top' showArrow={true} shouldFlip={true} closeDelay={100}>
                      <button className='btn-h p-1 m-0' onClick={addSourceFromSessionStorage}><PlusSquareIcon size={20} />
                      </button>
                    </Tooltip>
                  </div>}
              </div>}
            {newSources && newSources.length < 1 && <div className='text-white/100 font-light tracking-wider text-[14px] mt-2 p-0 italic'>No sources specified</div>}
          </>}
        <ol className='box-border flex flex-col gap-1 w-full p-0 m-0 sm:max-h-[100px] overflow-auto custom-scrollbar2 mt-1 list-decimal list-outside pr-2'>
          {newSources && newSources.length > 0 && newSources.map((sourceObject: SourceObject, index: number) => <li key={index} className='box-border flex flex-row justify-left items-center gap-1 w-full p-0 m-0 rounded-sm border-1 border-white/5 border-solid'>
              <div className='flex flex-row justify-left items-center gap-1 w-full p-0 m-0'>
                <div className='box-border flex items-center w-full'>
                  <div aria-label={`Source ${index + 1}`} className="cursor-default box-border flex items-center max-h-[30px] bg-transparent p-2 truncate ... overflow-hidden resize-none text-white/65 outline-none">
                    {sourceObject.truncatedFilePath ? sourceObject.truncatedFilePath.split('/').pop() : sourceObject.sourceUrl ? sourceObject.sourceUrl.split('/').pop() : 'Unknown'}
                    {formatSimplifiedSourceRowSuffix(sourceObject)}
                  </div>
                </div>
                <div className='flex flex-row justify-end items-center w-full p-0 m-0 gap-2'>
                  <Popover placement='left-end' showArrow={false} shouldFlip={true} offset={10} crossOffset={150} autoFocus={true} className="outline-none" classNames={{
                base: `outline-none before:bg-gray-600 before:rounded-[2px] before:shadow-none before:drop-shadow-none
                      border-0 before:w-[25px] before:h-[25px] before:mt-[23px] before:mb-[-23px] mr-[-88px] sm:mr-0 h-screen sm:h-auto w-screen sm:w-auto`
              }} isOpen={previewPopoverOpen === index} onOpenChange={open => {
                if (!open) {
                  setPreviewPopoverOpen(-1);
                }
              }} onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setPreviewPopoverOpen(-1);
                }
              }}>
                    <PopoverTrigger asChild>
                      <button type="button" aria-label={`Preview source ${index + 1}`} aria-haspopup="dialog" disabled={!UsableMediaTypes.includes(getMediaType(sourceObject, sourceObject.sourceInfo))} className={`btn-h box-border flex justify-center items-center p-0 sm:p-1 m-0
                          ${UsableMediaTypes.includes(getMediaType(sourceObject, sourceObject.sourceInfo)) ? 'cursor-pointer' : 'cursor-not-allowed'}`} onClick={e => {
                    e.stopPropagation();
                    if (UsableMediaTypes.includes(getMediaType(sourceObject, sourceObject.sourceInfo))) {
                      setPreviewPopoverOpen(index);
                    }
                  }}>
                        {previewPopoverOpen === -1 ? <Tooltip content="View" placement="left" showArrow={true} shouldFlip={true} closeDelay={100} delay={500} classNames={{
                      base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px] bg-tooltip-bg rounded-sm p-2',
                      content: 'bg-transparent shadow-none p-0 m-0'
                    }}>
                            <EyeIcon size={20} className='outline-none' />
                          </Tooltip> : <EyeIcon size={20} className='outline-none' />}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-[450px] bg-[#2C3A4A] rounded-md border-1 border-gray-800 border-solid">
                      <div tabIndex={0} className="flex flex-col justify-start items-center gap-2 w-[450px] h-[640px] outline-none" onKeyDown={e => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setPreviewPopoverOpen(-1);
                    }
                  }}>
                        <div className="flex flex-row justify-between items-center gap-1">
                          <div className="text-white/100 text-sm break-all w-[calc(100%-30px)] bg-gray-900 rounded-sm my-1 mx-2 p-1">
                            {extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown', true).truncatedFilePath}
                          </div>
                          <div className="cursor-pointer h-[16px]" onClick={e => {
                        e.stopPropagation();
                        setPreviewPopoverOpen(-1);
                      }}>
                            <XIcon className="pr-[5px] mt-[-10px]" size={16} />
                          </div>
                        </div>
                        <div className="previewContainer flex flex-row justify-center items-center w-[420px] min-w-[420px] h-[540px] min-h-[540px]">
                          <MediaDisplay mediaItem={{
                        mediaType: getMediaType(sourceObject, sourceObject.sourceInfo),
                        mediaUrl: sourceObject.sourceUrl,
                        mediaSrc: sourceObject.sourceUrl,
                        mediaContext: sourceObject.sourceContext
                      } as MediaObject} containerHeight={520} containerWidth={420} isPreview={false} />
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Tooltip content="Download" placement="right" showArrow={true} shouldFlip={true} closeDelay={100} delay={500} classNames={{
                base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px] bg-tooltip-bg rounded-sm p-2',
                content: 'bg-transparent shadow-none p-0 m-0'
              }}>
                    <button className={`
                        btn-h box-border flex justify-center items-center p-0 sm:p-1 m-0 hover:text-white
                        ${isRestrictedFileType(sourceObject) ? 'text-white/20 cursor-not-allowed' : 'text-white/65 cursor-pointer'}
                      `} onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDownload(sourceObject, !!featureFlags?.useIntercom);
                }} disabled={isRestrictedFileType(sourceObject)}>
                      <DownloadSimpleIcon size={20} className='outline-none' />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </li>)}
        </ol>
      </div>;
  }
  return <div className='flex line-clamp-1 max-h-[30px]' data-sentry-component="SimplifiedSources" data-sentry-source-file="simplifiedSources.tsx">
      {newSources.length && newSources.length > 0 ? <span className='flex justify-left p-0 m-0'>
          {`${newSources.length} sources`}
        </span> : <span className='flex justify-left p-0 m-0'>No sources specified</span>}
    </div>;
};
SimplifiedSources.displayName = 'Sources';
export default SimplifiedSources;